<template>
    <div class="nc-wrapper">
        <div>
            <el-input
                class="nc-search"
                placeholder="Search"
                size="small"
                v-model="searchTerm"
            ></el-input>
            <v-icon class="nc-settings" @click="openSettings">settings</v-icon>
        </div>
        <div class="nc-groups">
            <el-radio-group v-model="customType" size="small">
                <el-radio-button
                    :label="$t('form.label.notification.all')"
                ></el-radio-button>
                <el-radio-button
                    :label="$t('form.label.notification.wa')"
                ></el-radio-button>
                <el-radio-button
                    :label="$t('form.label.notification.blc')"
                ></el-radio-button>
                <el-radio-button
                    :label="$t('form.label.notification.warnings')"
                ></el-radio-button>
                <el-radio-button
                    :label="$t('form.label.notification.critical')"
                ></el-radio-button>
            </el-radio-group>
        </div>
        <div>
            <virtual-list
                class="list-infinite scroll-touch"
                :data-key="'id'"
                :data-sources="sortedMessages"
                :data-component="itemComponent"
                :estimate-size="70"
                :item-class="'list-item-infinite'"
                :footer-class="'loader-wrapper'"
                v-on:totop="onScrollToTop"
                v-on:tobottom="onScrollToBottom"
            >
                <div slot="footer" v-if="isLoading" class="loader"></div>
            </virtual-list>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import Notification from './Notification'
import VirtualList from 'vue-virtual-scroll-list'
import ChatMixin from '../../mixins/chat-mixin'
import { DateTime } from 'luxon'

export default {
    name: 'stc-notification-center',

    props: {
        notifications: {
            default: () => [],
        },
    },

    data() {
        return {
            customType: 'ALL',
            searchTerm: '',
            itemComponent: Notification,
            items: [],
            currentItems: [],
            isLoading: false,
            currentPage: 1,
            isPreviousMsgFetchNeeded: true,
        }
    },

    components: { 'virtual-list': VirtualList },

    computed: {
        searchOptions() {
            return {
                keys: ['MessageBody', 'Author', 'ChannelName', 'CreatedAt'],
            }
        },
        sortedMessages() {
            const sorted = this.items.sort(
                (a, b) => moment(b.CreatedAt) - moment(a.CreatedAt)
            )
            return sorted
        },
    },

    watch: {
        async customType(val) {
            this.items = []
            this.currentPage = 1
            this.isLoading = true
            await this.getUserMessages(val)
            this.isLoading = false
        },

        searchTerm: _.debounce(async function () {
            if (this.searchTerm !== '') {
                this.items = await this.$search(
                    this.searchTerm,
                    this.currentItems,
                    this.searchOptions
                )
                this.isLoading = false
            } else {
                this.items = this.currentItems
                this.isLoading = false
            }
        }, 500),

        notifications: function (notifications) {
            if (notifications && notifications.length > 0) {
                this.items = []
                this.currentItems = []
                notifications.forEach((notification) => {
                    const notificationObj = this.buildNotification(notification)
                    this.items.push(notificationObj)
                    this.currentItems.push(notificationObj)
                })
            }
        },
    },

    created() {
        this.isLoading = false
        Events.$on('nc-set-custom-type', () => {
            const allType = 'ALL'
            if (this.customType === allType) {
                this.getUserMessages(allType)
            }
            this.customType = allType
        })
    },

    mixins: [ChatMixin],

    methods: {
        onScrollToTop() {},

        async onScrollToBottom() {
            if (this.isPreviousMsgFetchNeeded && !this.isLoading) {
                this.isLoading = true
                this.currentPage++

                const notifications = await this.fetchUserMessages(
                    this.currentPage,
                    this.getCustomType(this.customType)
                )
                this.isPreviousMsgFetchNeeded = !(
                    !notifications || notifications.length === 0
                )
                this.isLoading = false
                if (notifications.length > 0) {
                    const mapped = notifications.map((notification) =>
                        this.buildNotification(notification)
                    )
                    this.items.push(...mapped)
                } else {
                    this.currentPage--
                }
            } else {
                this.isLoading = false
                return
            }
        },

        getCustomType(type) {
            switch (type) {
                case 'WA':
                    type = 'WANOTIFICATION'
                    break
                case 'BLC':
                    type = 'BLCNOTIFICATION'
                    break
                case 'Warnings':
                    type = 'WARNING'
                    break
                case 'Critical':
                    type = 'CRITICAL'
                    break
                default:
                    break
            }
            return type
        },

        openSettings() {
            Events.$emit('show-notification-settings', true)
        },

        buildNotification(notification) {
            const createdAt = notification.CreatedAt
                ? moment(
                      DateTime.fromISO(notification.CreatedAt, {
                          zone: 'utc',
                      }).toJSDate()
                  ).format('YYYY-MM-DD HH:mm:ss')
                : moment().format('YYYY-MM-DD HH:mm')
            return {
                index: 0,
                name: '',
                id: Math.floor(Math.random() * 10000),
                desc: '',
                MessageBody: notification.Message,
                CreatedAt: createdAt,
                Author: notification.Sender
                    ? notification.Sender.FirstName +
                      ' ' +
                      notification.Sender.LastName
                    : null,
                ChannelId: notification.ChannelId,
                ChannelName: notification.ChannelName,
                Title: notification.Title,
                Type: notification.Type,
                CustomType: notification.CustomType,
                ShortCode: notification.ShortCode,
                Data: notification.Data,
                IsRead: notification.IsRead,
            }
        },

        async getUserMessages(val) {
            const response = await this.fetchUserMessages(
                this.currentPage,
                this.getCustomType(val)
            )
            if (response && response.length > 0) {
                this.items = response.map((notification) =>
                    this.buildNotification(notification)
                )
            }
        },
    },
}
</script>
<style>
.nc-wrapper {
    background: #39424f;
    color: #ffffff;
    padding: 10px;
    min-width: 350px;
    border: 1px solid #e6e9ee;
}

.nc-groups {
    margin: 10px 0;
}

.nc-search {
    width: 318px;
}

.nc-settings {
    margin-left: 10px;
    cursor: pointer;
}

.nc-settings:hover {
    color: #409eff;
}

.list-infinite {
    width: 100%;
    min-height: 200px;
    max-height: 400px;
    border: 2px solid;
    border-radius: 3px;
    overflow-y: auto;
    border-color: #b3b7bd1a;
    position: relative;
}

.list-item-infinite {
    display: flex;
    align-items: center;
    padding: 1em;
    border-bottom: 1px solid;
    border-color: lightgray;
}

.loader-wrapper {
    padding: 1em;
}

.loader {
    font-size: 10px;
    margin: 0px auto;
    text-indent: -9999em;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #39424f;
    background: linear-gradient(
        to right,
        #34c7b3 10%,
        rgba(255, 255, 255, 0) 42%
    );
    position: relative;
    animation: load3 1.4s infinite linear;
    transform: translateZ(0);
}

.loader:before {
    width: 50%;
    height: 50%;
    background: #34c7b3;
    border-radius: 100% 0 0 0;
    position: absolute;
    top: 0;
    left: 0;
    content: '';
}
.loader:after {
    background: #39424f;
    width: 75%;
    height: 75%;
    border-radius: 50%;
    content: '';
    margin: auto;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
}
@-webkit-keyframes load3 {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
@keyframes load3 {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>